import { Box, Typography } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { useEffect, useMemo, useState } from "react";
import { setStudyStatus, StudyStatuses, StudyStatusesEnum } from "../../api/study";
import { MButton } from "../MButton";
import MModal from "../MModal";
import MSelect from "../MSelect";
import MStatusPlates from "../MStatusPlates";
import MTextarea from "../MTextarea";
import { studyStatuses } from "../../utils/constants";

interface StudyStatusChangeModalProps {
  open: boolean;
  studyId: string;
  currentStatus: StudyStatuses;
  onClose: () => void;
  onChanged: () => Promise<void> | void;
}

export const StudyStatusChangeModal = ({
  open,
  studyId,
  currentStatus,
  onClose,
  onChanged,
}: StudyStatusChangeModalProps) => {
  const [newStatus, setNewStatus] = useState<string>("");
  const [comment, setComment] = useState<string>("");
  const [busy, setBusy] = useState<boolean>(false);

  const statusOptions = useMemo(
    () => studyStatuses.filter((status) => status.value !== currentStatus),
    [currentStatus]
  );

  const isSubmitDisabled = !newStatus || !comment.trim() || newStatus === currentStatus;

  useEffect(() => {
    if (!open) return;

    setNewStatus("");
    setComment("");
  }, [open]);

  const handleSubmit = async () => {
    if (isSubmitDisabled) return;

    setBusy(true);
    try {
      await setStudyStatus({
        study_id: studyId,
        status: newStatus as StudyStatusesEnum,
        comment: comment.trim(),
      });
      await onChanged();
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <MModal open={open} handleClose={onClose} width={560} height={430}>
      <Box width="100%">
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
          Change study status
        </Typography>

        <Box display="flex" alignItems="center" mb={3}>
          <MStatusPlates value={currentStatus} />
          <Typography sx={{ mx: 2, fontWeight: 700 }}>-&gt;</Typography>
          {newStatus ? (
            <MStatusPlates value={newStatus as StudyStatuses} />
          ) : (
            <Typography color="text.secondary">Select new status</Typography>
          )}
        </Box>

        <Box mb={2}>
          <MSelect
            label="New status"
            value={newStatus}
            options={statusOptions}
            onChange={(e: SelectChangeEvent) => setNewStatus(e.target.value)}
          />
        </Box>

        <Box mb={3}>
          <MTextarea
            key={`${open}-${studyId}`}
            label="Reason/comment"
            placeholder="Reason for status change"
            value={comment}
            onChange={setComment}
            maxLength={500}
          />
        </Box>

        <Box display="flex" justifyContent="flex-end" gap={1}>
          <MButton variant="text" color="error" onClick={onClose} disabled={busy}>
            Cancel
          </MButton>
          <MButton busy={busy} disabled={isSubmitDisabled} onClick={handleSubmit}>
            Confirm
          </MButton>
        </Box>
      </Box>
    </MModal>
  );
};

export default StudyStatusChangeModal;
